/**
 * Operações imutáveis sobre WorkflowGraph.
 * Manipulam nodes/edges/snippets sem mutação direta.
 */

import { nanoid } from 'nanoid';
import type { WorkflowCatalog } from '../api/types';
import type {
  EdgeStyle,
  NodeKind,
  NodeConfigByKind,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
} from '../types';
import {
  defaultConfigFor,
  edgeStyleForPort,
  outputPortIds,
  primaryOutputPortId,
} from './catalog';

export const newId = (prefix = 'n') => `${prefix}_${nanoid(8)}`;

/**
 * Config de um passo novo e as saídas de cada tipo vêm do catálogo
 * (`utils/catalog.ts`); quem chama passa o catálogo.
 */
export function makeNode<K extends NodeKind>(
  catalog: WorkflowCatalog,
  kind: K,
  position: { x: number; y: number } = { x: 0, y: 0 },
  config?: Partial<NodeConfigByKind[K]>,
  snippetId?: string,
): WorkflowNode<K> {
  return {
    id: newId(kind),
    kind,
    position,
    config: {
      ...defaultConfigFor(catalog, kind),
      ...(config as object),
    } as unknown as NodeConfigByKind[K],
    snippetId,
  };
}

export function addNode(
  graph: WorkflowGraph,
  node: WorkflowNode,
): WorkflowGraph {
  return { ...graph, nodes: [...graph.nodes, node] };
}

export function updateNode(
  graph: WorkflowGraph,
  nodeId: string,
  patch: Partial<WorkflowNode>,
): WorkflowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)),
  };
}

export function updateNodeConfig<K extends NodeKind>(
  graph: WorkflowGraph,
  nodeId: string,
  configPatch: Partial<NodeConfigByKind[K]>,
): WorkflowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) =>
      n.id === nodeId
        ? {
            ...n,
            config: {
              ...(n.config as object),
              ...(configPatch as object),
            } as NodeConfigByKind[NodeKind],
          }
        : n,
    ),
  };
}

/**
 * Tira as ligações que saíam de portas que o passo tinha em `before` e não
 * tem mais em `after` (regra do Acompanhar entrega apagada, item de lista
 * que vira porta). Ligação que já estava sem porta antes fica: o validador
 * acusa (`edge_port_missing`). Sem nada a tirar, devolve o mesmo grafo.
 */
export function dropEdgesOfVanishedPorts(
  catalog: WorkflowCatalog,
  graph: WorkflowGraph,
  before: WorkflowNode,
  after: WorkflowNode,
): WorkflowGraph {
  const now = new Set(outputPortIds(catalog, after));
  const gone = new Set(
    outputPortIds(catalog, before).filter((id) => !now.has(id)),
  );
  if (gone.size === 0) return graph;
  const vanished = (e: WorkflowEdge) =>
    e.fromNodeId === after.id && gone.has(e.fromPortId);
  if (!graph.edges.some(vanished)) return graph;
  return { ...graph, edges: graph.edges.filter((e) => !vanished(e)) };
}

export function removeNode(
  graph: WorkflowGraph,
  nodeId: string,
): WorkflowGraph {
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => n.id !== nodeId),
    edges: graph.edges.filter(
      (e) => e.fromNodeId !== nodeId && e.toNodeId !== nodeId,
    ),
    snippets: graph.snippets
      .map((s) => ({ ...s, nodeIds: s.nodeIds.filter((id) => id !== nodeId) }))
      .filter((s) => s.nodeIds.length > 0),
  };
}

export function addEdge(
  graph: WorkflowGraph,
  fromNodeId: string,
  fromPortId: string,
  toNodeId: string,
  style: EdgeStyle = 'default',
): WorkflowGraph {
  // dedup: se já existe edge da mesma porta, substitui
  const existing = graph.edges.findIndex(
    (e) => e.fromNodeId === fromNodeId && e.fromPortId === fromPortId,
  );
  const next: WorkflowEdge = {
    id: newId('edge'),
    fromNodeId,
    fromPortId,
    toNodeId,
    style,
  };
  const edges = [...graph.edges];
  if (existing >= 0) edges[existing] = next;
  else edges.push(next);
  return { ...graph, edges };
}

export function removeEdge(
  graph: WorkflowGraph,
  edgeId: string,
): WorkflowGraph {
  return { ...graph, edges: graph.edges.filter((e) => e.id !== edgeId) };
}

/**
 * Ligar `fromNodeId → toNodeId` faria o fluxo voltar e repetir sem fim? Sim
 * quando é o próprio passo ou quando, saindo de `toNodeId` pelas ligações,
 * dá para chegar em `fromNodeId`. `ignoreEdgeId` tira uma ligação da conta:
 * a que vai ser trocada (reconectar a ponta, trocar o destino da porta).
 * O back recusa o mesmo caso com `GRAPH_CYCLE`.
 */
export function wouldCreateCycle(
  graph: WorkflowGraph,
  fromNodeId: string,
  toNodeId: string,
  opts: { ignoreEdgeId?: string } = {},
): boolean {
  if (fromNodeId === toNodeId) return true;
  const next = new Map<string, string[]>();
  for (const e of graph.edges) {
    if (e.id === opts.ignoreEdgeId) continue;
    const list = next.get(e.fromNodeId);
    if (list) list.push(e.toNodeId);
    else next.set(e.fromNodeId, [e.toNodeId]);
  }
  const seen = new Set<string>([toNodeId]);
  const queue = [toNodeId];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    for (const to of next.get(id) ?? []) {
      if (to === fromNodeId) return true;
      if (seen.has(to)) continue;
      seen.add(to);
      queue.push(to);
    }
  }
  return false;
}

/** A ligação que sai hoje da porta (`addEdge` troca ela pela nova). */
export const edgeFromPort = (
  graph: WorkflowGraph,
  fromNodeId: string,
  fromPortId: string,
): WorkflowEdge | undefined =>
  graph.edges.find(
    (e) => e.fromNodeId === fromNodeId && e.fromPortId === fromPortId,
  );

/**
 * `wouldCreateCycle` para ligar uma PORTA a um passo: a ligação que a porta
 * já tem sai da conta, porque `addEdge` troca ela pela nova.
 */
export const portLinkWouldCycle = (
  graph: WorkflowGraph,
  fromNodeId: string,
  fromPortId: string,
  toNodeId: string,
): boolean =>
  wouldCreateCycle(graph, fromNodeId, toNodeId, {
    ignoreEdgeId: edgeFromPort(graph, fromNodeId, fromPortId)?.id,
  });

/**
 * Cria `node` ligado à porta `fromPortId` de `fromNodeId`. Se a porta já
 * levava a um passo B, o novo entra NO MEIO (origem → novo → B): a ligação
 * antiga passa a sair da saída principal do novo nó em vez de ser perdida.
 */
export function insertNodeFromPort(
  catalog: WorkflowCatalog,
  graph: WorkflowGraph,
  fromNodeId: string,
  fromPortId: string,
  node: WorkflowNode,
): WorkflowGraph {
  const from = graph.nodes.find((n) => n.id === fromNodeId);
  if (!from) return addNode(graph, node);
  const previous = graph.edges.find(
    (e) => e.fromNodeId === fromNodeId && e.fromPortId === fromPortId,
  );
  let next = addEdge(
    addNode(graph, node),
    fromNodeId,
    fromPortId,
    node.id,
    edgeStyleForPort(catalog, from, fromPortId),
  );
  if (previous) {
    const outPort = primaryOutputPortId(catalog, node);
    next = addEdge(
      next,
      node.id,
      outPort,
      previous.toNodeId,
      edgeStyleForPort(catalog, node, outPort),
    );
  }
  return next;
}
